// Miko Labs – Schalter „Neue Ansicht“ im Mitgliederbereich (Etappe 8, Schritt 4a,
// docs/labs.md „Neue Ansicht im Mitgliederbereich“).
//
// Mit Schalter zeichnet `StudentLayout` statt der gewohnten Seitenleiste die
// Hülle „Lebendig“ aus dem Labor um dieselben Seiten. Für Mitglieder ändert
// sich nichts, auch keine zusätzliche Abfrage: Der Server wird nur gefragt,
// wenn das Konto eine Admin-Rolle hat (das weiß `AccessContext` ohnehin) oder
// wenn in diesem Browser schon eine Wahl für genau dieses Konto liegt (oder
// eine Einladung `?ansicht=neu` in der Adresse steht). Ob es den Schalter
// geben darf, entscheidet allein der Server (`labs_funktion_freigegeben`).
//
// Hier nur Regeln und der Speicher im Browser, ohne Oberfläche, damit sie
// testbar sind. Der Hook steht in `src/components/labs/neueAnsicht/`.

import { safeGetItem, safeRemoveItem, safeSessionGetItem, safeSetItem } from "@/lib/safeStorage";

/** Schlüssel in `labs_funktionen`; die Freigabe dort bestimmt, wer den Schalter sieht. */
export const NEUE_ANSICHT_KEY = "neue_ansicht";

/** Adresse mit `?ansicht=neu` schaltet die neue Ansicht ein, wenn der Server sie erlaubt (Testgruppe). */
export const EINLADUNG_PARAMETER = "ansicht";
export const EINLADUNG_WERT = "neu";

export type AnsichtWahl = "neu" | "gewohnt";

/**
 * Schalter „Neue Ansicht“ je Person im Admin (Sitzung N, Daniel 20.10.2026: „Ich möchte das manuell bestimmen.“):
 * `labs_neue_ansicht_fest_setzen()` legt `neue_ansicht: "fest"` in `app_metadata` ab und gibt dem Konto eine Zeile in
 * `labs_zugang`. Das Merkmal steht im Token, gelesen wird es ohne Abfrage; es wirkt wie eine Einladung bei jedem Laden,
 * eine eigene Wahl „gewohnt“ gilt dann nicht. Neu im Token ist es beim nächsten Anmelden, sonst nach spätestens einer
 * Stunde (Erneuern des Tokens).
 */
export const FEST_SCHLUESSEL = "neue_ansicht";

export function festAusMetadaten(appMetadata: unknown): boolean {
  if (!appMetadata || typeof appMetadata !== "object") return false;
  return (appMetadata as Record<string, unknown>)[FEST_SCHLUESSEL] === "fest";
}

/**
 * Umzug für alle (Sitzung 4, Daniel 14.10.2026: „Nach dem Umzug sehen alle nur die neue Ansicht, einen Weg zurück gibt
 * es nicht“). Steht hier `false`, gilt alles wie bisher, und für Mitglieder ändert sich nichts, auch keine Abfrage.
 * Mit `true` sieht jedes angemeldete Konto die neue Ansicht (auch in einer Übernahme, Frage 284), die Knöpfe
 * „Neue Ansicht“ und „Zur gewohnten Ansicht“ entfallen, und jedes Konto fragt einmal je Sitzung
 * `labs_funktion_freigegeben('neue_ansicht')`. Erst mit `freigabe = 'live'` in `labs_funktionen` sagt der Server für alle
 * ja; zurück auf `beta` ist der Not-Aus ohne Veröffentlichen (docs/labs.md, „Umzug für alle“).
 *
 * Umgestellt auf Daniels ausdrückliches „Start“ (Start-Commit, Sitzung SB; ein Test hält den Wert fest). Zurück auf den
 * alten Stand nur mit `false` und Veröffentlichen; der Not-Aus ohne Veröffentlichen ist `neue_ansicht` auf `beta`.
 */
export const UMZUG_FUER_ALLE = true;

/**
 * Die Wahl liegt je Konto im `localStorage`. Je Konto, weil eine Übernahme
 * („Als Nutzer anmelden“) im anderen Tab denselben `localStorage` sieht; nur
 * die Anmeldung (`sb-*`) wird dort umgeleitet.
 */
export function wahlSchluessel(userId: string): string {
  return `mk-neue-ansicht:${userId}`;
}

export function wahlAusWert(wert: string | null | undefined): AnsichtWahl | null {
  return wert === "neu" || wert === "gewohnt" ? wert : null;
}

export function einladungIn(suche: string): boolean {
  try {
    return new URLSearchParams(suche).get(EINLADUNG_PARAMETER) === EINLADUNG_WERT;
  } catch {
    return false;
  }
}

/**
 * Muss der Server gefragt werden? Nur bei einer Admin-Rolle, einer Wahl in
 * diesem Browser oder einer Einladung in der Adresse. Ein Mitglied ohne all
 * das bekommt keine einzige zusätzliche Abfrage.
 */
export function sollServerFragen(lage: { istAdmin: boolean; wahl: AnsichtWahl | null; einladung: boolean }): boolean {
  return lage.istAdmin || lage.wahl !== null || lage.einladung;
}

/**
 * Läuft die neue Ansicht? Nur mit der Wahl „neu“. Solange die Antwort des
 * Servers aussteht, gilt die Wahl (sonst blitzt bei jedem Laden kurz die
 * gewohnte Seitenleiste auf); sagt der Server nein oder scheitert die Abfrage,
 * gilt die gewohnte Ansicht.
 */
export function ansichtAktiv(lage: { wahl: AnsichtWahl | null; freigabe: boolean | undefined; fehler: boolean }): boolean {
  return lage.wahl === "neu" && lage.freigabe !== false && !lage.fehler;
}

/**
 * Nach dem Umzug (`UMZUG_FUER_ALLE`): Die Antwort des Servers entscheidet. Solange sie aussteht oder die Abfrage
 * scheitert, gilt die letzte bekannte Antwort, die der Browser sich merkt (`gemerkt`); ohne Merkzettel die neue
 * Ansicht. So blitzt beim Laden nichts auf, und ein Netzfehler wirft niemanden in die gewohnte Ansicht. Nur ein
 * ausdrückliches Nein des Servers (Not-Aus) schaltet zurück.
 */
export function ansichtAktivNachUmzug(lage: { gemerkt: AnsichtWahl | null; freigabe: boolean | undefined }): boolean {
  if (lage.freigabe !== undefined) return lage.freigabe;
  return lage.gemerkt !== "gewohnt";
}

/**
 * Zeigt die Hülle „Zur gewohnten Ansicht“? Nach dem Umzug nie (Daniel, 14.10.2026: keinen Weg zurück, für niemanden).
 * Bis dahin nur für das Team (Stufe `labs`, `ist_labs_tester()`), das beim Testen umschaltet; eine Testgruppe (Stufe
 * `beta`) sieht schon wie nach dem Umzug keinen Weg zurück. Die Führung zeigt nie auf den Knopf.
 */
export function rueckwegZeigen(lage: { umzug: boolean; team: boolean }): boolean {
  return !lage.umzug && lage.team;
}

// ─── Speicher im Browser ──────────────────────────────────────────────────

const EREIGNIS = "mk-neue-ansicht";

export function wahlLesen(userId: string | null | undefined): AnsichtWahl | null {
  if (!userId) return null;
  return wahlAusWert(safeGetItem(wahlSchluessel(userId)));
}

/** Speichert die Wahl (oder löscht sie mit `null`) und sagt allen Stellen in diesem Tab Bescheid. */
export function wahlSetzen(userId: string, wahl: AnsichtWahl | null): void {
  if (wahl) safeSetItem(wahlSchluessel(userId), wahl);
  else safeRemoveItem(wahlSchluessel(userId));
  try {
    window.dispatchEvent(new Event(EREIGNIS));
  } catch {
    // ohne window (Tests ohne DOM) gibt es nichts zu benachrichtigen
  }
}

/** Für `useSyncExternalStore`: dieser Tab (eigenes Ereignis) und andere Tabs (`storage`). */
export function wahlAbonnieren(rueckruf: () => void): () => void {
  window.addEventListener(EREIGNIS, rueckruf);
  window.addEventListener("storage", rueckruf);
  return () => {
    window.removeEventListener(EREIGNIS, rueckruf);
    window.removeEventListener("storage", rueckruf);
  };
}

/**
 * Läuft in diesem Tab eine Übernahme („Als Nutzer anmelden“)? Die Administration
 * will sehen, was das Mitglied sieht (Frage 284): Bis zum Umzug bleibt es deshalb
 * bei der gewohnten Ansicht, danach gilt die neue, mit dem Warnband
 * `UebernahmeBand` statt dem aus `StudentLayout`. Gespeichert wird in einer
 * Übernahme nichts, und die Führung startet nie.
 */
export function istUebernahme(): boolean {
  return safeSessionGetItem("isImpersonating") === "true";
}
